import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import { ImportStepper } from '@ooh/ui/components/import-stepper';
import type { AvailabilityImportUploadResponse } from '@ooh/contracts';
import { useHasPermission } from '../../plugs/current-user';
import { JobNotFoundState } from '../../media-import/components/job-not-found-state';
import { useJobNotFound } from '../hooks/use-job-not-found';
import { UploadPhase } from './upload-phase';
import { HeaderRowPhase } from './header-row-phase';
import { PreviewPhase } from './preview-phase';
import { ResultPhase } from './result-phase';

export type AvailabilityImportPhase = 'upload' | 'header-row' | 'preview' | 'result';

const PHASE_ORDER: AvailabilityImportPhase[] = ['upload', 'header-row', 'preview', 'result'];

interface AvailabilityImportWizardProps {
  phase: AvailabilityImportPhase;
  jobId: number | null;
  onUploadNext: (jobId: number) => void;
  onHeaderRowNext: () => void;
  onConfirmed: () => void;
  onBackToUpload: () => void;
  /** Result step "Finish"; defaults to `onBackToUpload`. */
  onFinish?: () => void;
}

interface UploadState {
  response: AvailabilityImportUploadResponse;
  fileName: string;
  fileSizeKb: number;
}

/** Dispatches to the active phase's component. Extracted to keep the parent
 * component's complexity under the repo's per-function budget. */
function renderPhase(
  phase: AvailabilityImportPhase,
  jobId: number | null,
  uploadState: UploadState | null,
  headerRowIndex: number | undefined,
  isJobNotFound: boolean,
  handlers: {
    onUploaded: (response: AvailabilityImportUploadResponse, file: File) => void;
    onHeaderRowNext: (chosenHeaderRowIndex: number | undefined) => void;
    onBackToUpload: () => void;
    onConfirmed: () => void;
    onFinish: () => void;
  },
): React.ReactNode {
  if (phase === 'upload') return <UploadPhase onNext={handlers.onUploaded} />;
  if (isJobNotFound) return <JobNotFoundState onBackToImport={handlers.onBackToUpload} />;
  if (phase === 'header-row' && jobId !== null && uploadState !== null) {
    return (
      <HeaderRowPhase
        jobId={jobId}
        uploadResponse={uploadState.response}
        fileName={uploadState.fileName}
        fileSizeKb={uploadState.fileSizeKb}
        onBack={handlers.onBackToUpload}
        onNext={handlers.onHeaderRowNext}
      />
    );
  }
  if (phase === 'preview' && jobId !== null) {
    return (
      <PreviewPhase
        jobId={jobId}
        headerRowIndex={headerRowIndex}
        onBack={handlers.onBackToUpload}
        onConfirmed={handlers.onConfirmed}
      />
    );
  }
  if (phase === 'result' && jobId !== null) {
    return (
      <ResultPhase
        importJobId={jobId}
        onFinish={handlers.onFinish}
        onRetry={handlers.onBackToUpload}
      />
    );
  }
  return null;
}

/**
 * The availability-import screen: renders an
 * access-denied notice for callers lacking `media_unit.block_availability`
 * (per availability-import-ui's "Caller without permission does not see
 * the entry point" scenario — direct navigation shows this, not a redirect),
 * otherwise dispatches to the active wizard phase.
 *
 * Retains the just-completed upload's full response (+ the selected file's
 * name/size for the header-row step's file-summary bar) in local state
 * across the upload -> header-row -> preview phase transitions (in-app
 * navigation, no full reload) — the header-row phase needs
 * rawRows/detectedHeaderRowIndex from it, and no endpoint re-exposes those
 * by job id alone (see findings.md). The preview phase no longer reads
 * anything from this upload-time snapshot: `GET .../preview` itself returns
 * fresh `headers`/`sampleRows`/`columnDistinctValues` parsed against
 * whichever header row was actually used (QA bugfix round 2, CU-86cb1npz2)
 * — sourcing them from `uploadState` instead would silently key every
 * column-mapping sample value and status-token lookup by the WRONG
 * (auto-detected, pre-override) row.
 *
 * `headerRowIndex` (CU-86cb1npz2, gap #1) is the operator's manual
 * header-row override, chosen on the header-row phase and forwarded to
 * `PreviewPhase` so its own preview fetch lands in the same query-cache slot
 * the header-row phase's prefetch already populated (see
 * `usePreviewWithHeaderRowOverride`) — `undefined` for the common case
 * where the auto-detected row was kept.
 *
 * The result phase's "finish" and "retry" actions both reuse
 * `onBackToUpload` (finish unless `onFinish` is given) — there is no worker-side mechanism to resume/retry a
 * failed job in place, so the safest action for either is to start a fresh
 * upload, mirroring the mockup's distinct-but-equivalent "Zakończ"/"Spróbuj
 * ponownie" buttons.
 *
 * `uploadState` lives only in this component's React state, so a page
 * reload or a direct `?phase=header-row&importJobId=<id>` visit (no in-app
 * upload step) leaves it `null` on mount — `HeaderRowPhase` still needs its
 * rawRows/detectedHeaderRowIndex, which no endpoint re-exposes by job id
 * alone (findings.md), so the only safe recovery for that phase is to
 * redirect back to upload. `sessionLost` also covers `'preview'` for the
 * same direct-URL-visit UX (avoids briefly flashing a preview whose
 * `headerRowIndex` override was lost), even though `PreviewPhase` itself no
 * longer technically depends on `uploadState`.
 */
export function AvailabilityImportWizard({
  phase,
  jobId,
  onUploadNext,
  onHeaderRowNext,
  onConfirmed,
  onBackToUpload,
  onFinish = onBackToUpload,
}: AvailabilityImportWizardProps) {
  const { t } = useTranslation('availabilityImport');
  const canBlockAvailability = useHasPermission('media_unit.block_availability');
  const isJobNotFound = useJobNotFound(phase, jobId);
  const [uploadState, setUploadState] = React.useState<UploadState | null>(null);
  const [headerRowIndex, setHeaderRowIndex] = React.useState<number | undefined>(undefined);
  const sessionLost =
    (phase === 'header-row' || phase === 'preview') && jobId !== null && uploadState === null;

  function handleUploaded(response: AvailabilityImportUploadResponse, file: File) {
    setUploadState({
      response,
      fileName: file.name,
      fileSizeKb: Math.round(file.size / 1024),
    });
    onUploadNext(response.importJobId);
  }

  function handleHeaderRowNext(chosenHeaderRowIndex: number | undefined) {
    setHeaderRowIndex(chosenHeaderRowIndex);
    onHeaderRowNext();
  }

  React.useEffect(() => {
    if (!sessionLost) return;
    toast.error(t('toasts.sessionLost'));
    onBackToUpload();
  }, [sessionLost, onBackToUpload, t]);

  if (!canBlockAvailability) {
    return (
      <div
        className="flex items-center justify-center h-full py-20"
        data-testid="availability-import-access-denied"
      >
        <p className="text-muted-foreground">{t('accessDenied')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="availability-import">
      <div className="shrink-0 px-6 pt-6 pb-4 border-b border-border/60">
        <div className="mb-4">
          <h1 className="text-[15px] font-semibold" data-testid="page-heading">
            {t('pageTitle')}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">{t('pageSubtitle')}</p>
        </div>
        {!isJobNotFound && (
          <ImportStepper
            steps={[
              t('stepper.upload'),
              t('stepper.headerRow'),
              t('stepper.preview'),
              t('stepper.result'),
            ]}
            currentStep={PHASE_ORDER.indexOf(phase)}
            ariaLabel={t('pageTitle')}
          />
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        {sessionLost
          ? null
          : renderPhase(phase, jobId, uploadState, headerRowIndex, isJobNotFound, {
              onUploaded: handleUploaded,
              onHeaderRowNext: handleHeaderRowNext,
              onBackToUpload,
              onConfirmed,
              onFinish,
            })}
      </div>
    </div>
  );
}
